import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Receipt } from 'lucide-react'

import { SalonReportFilterBar } from '@/components/reports/SalonReportFilterBar'
import { SaleDocumentPrintHosts } from '@/components/sales/SaleDocumentPrintHosts'
import { SalonDataTable } from '@/components/ui/SalonDataTable'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import { searchCustomers } from '@/lib/api/customers'
import { fetchSaleHistory } from '@/lib/api/saleHistory'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useSaleDocumentPrint } from '@/hooks/useSaleDocumentPrint'
import { resolveReportPeriodBounds } from '@/lib/reports/filters'
import {
  formatSaleHistoryDate,
  formatSaleHistoryGuest,
  formatSaleHistoryPayment,
  formatSaleHistoryServices,
} from '@/lib/sales/saleHistoryDisplay'

/**
 * @typedef {'sale' | 'line'} SaleHistoryRowMode
 */

/**
 * @param {object} props
 * @param {SaleHistoryRowMode} [props.rowMode='sale']
 * @param {boolean} [props.showCustomerFilter=true]
 * @param {boolean} [props.showSearch=true]
 * @param {number | null} [props.stylistId]
 * @param {boolean} [props.requireStylist=false]
 * @param {string} [props.emptyLabel]
 * @param {string} [props.tableTitle='Completed tickets']
 */
export function SaleHistoryDataTable({
  rowMode = 'sale',
  showCustomerFilter = true,
  showSearch = true,
  stylistId = null,
  requireStylist = false,
  emptyLabel = 'No completed tickets match these filters.',
  tableTitle = 'Completed tickets',
}) {
  const { activeBranchId } = useBranch()
  const [period, setPeriod] = useState('month')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [search, setSearch] = useState('')
  const [customerId, setCustomerId] = useState(null)
  const [customerLabel, setCustomerLabel] = useState('')
  const [customerQuery, setCustomerQuery] = useState('')
  const [customerOptions, setCustomerOptions] = useState([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  /** @type {[Awaited<ReturnType<typeof fetchSaleHistory>> | null, Function]} */
  const [result, setResult] = useState(null)

  const {
    reprintReceipt,
    busySaleId,
    busyAction,
    error: printError,
    receipt,
    taxInvoice,
    receiptRootRef,
    taxInvoiceRootRef,
  } = useSaleDocumentPrint()

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const loadHistory = useCallback(async () => {
    if (requireStylist && !stylistId) {
      setResult(null)
      setLoading(false)
      return
    }

    if (period === 'custom' && (!customFrom || !customTo)) {
      setResult(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchSaleHistory({
        period,
        customFrom,
        customTo,
        customerId,
        stylistId,
        search: showSearch ? search : undefined,
        page,
      })
      setResult(data)
    } catch (err) {
      setResult(null)
      setError(apiErrorMessage(err, 'Could not load ticket history.'))
    } finally {
      setLoading(false)
    }
  }, [
    period,
    customFrom,
    customTo,
    customerId,
    stylistId,
    search,
    page,
    requireStylist,
    showSearch,
  ])

  useEffect(() => {
    setPage(1)
  }, [period, customFrom, customTo, customerId, stylistId, search, activeBranchId])

  useEffect(() => {
    loadHistory()
  }, [loadHistory, activeBranchId])

  useEffect(() => {
    if (!showCustomerFilter || customerQuery.trim().length < 2) {
      setCustomerOptions([])
      return
    }

    let active = true
    const timer = window.setTimeout(async () => {
      try {
        const items = await searchCustomers(customerQuery.trim(), { limit: 8 })
        if (active) setCustomerOptions(items)
      } catch {
        if (active) setCustomerOptions([])
      }
    }, 250)

    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [customerQuery, showCustomerFilter])

  const sales = result?.sales ?? []
  const meta = result?.meta ?? { current_page: 1, last_page: 1, total: 0 }
  const currency = sales[0]?.currency_code ?? 'AED'

  const tableRows = useMemo(() => {
    if (rowMode === 'sale') {
      return sales
    }

    return sales.flatMap((sale) =>
      (sale.services ?? []).map((line, index) => ({
        rowKey: `${sale.id}-${index}`,
        sale_id: sale.id,
        sale_number: sale.sale_number,
        completed_at: sale.completed_at,
        customer: sale.customer,
        currency_code: sale.currency_code,
        payments: sale.payments,
        service_name: line.description,
        line_total: line.line_total,
        stylist_name: line.stylist_name,
      })),
    )
  }, [rowMode, sales])

  function clearCustomerFilter() {
    setCustomerId(null)
    setCustomerLabel('')
    setCustomerQuery('')
    setCustomerOptions([])
  }

  function selectCustomer(customer) {
    setCustomerId(customer.id)
    setCustomerLabel(customer.name)
    setCustomerQuery('')
    setCustomerOptions([])
  }

  const columns =
    rowMode === 'line'
      ? [
          {
            key: 'completed_at',
            label: 'Date',
            render: (row) => (
              <span className="whitespace-nowrap">{formatSaleHistoryDate(row.completed_at)}</span>
            ),
          },
          {
            key: 'sale_number',
            label: 'Receipt',
            render: (row) => <span className="font-medium">#{row.sale_number}</span>,
          },
          {
            key: 'customer',
            label: 'Guest',
            render: (row) => formatSaleHistoryGuest(row.customer),
          },
          {
            key: 'service_name',
            label: 'Service',
            render: (row) => row.service_name,
          },
          {
            key: 'line_total',
            label: 'Amount',
            className: 'text-right font-mono tabular-nums',
            headerClassName: 'text-right',
            render: (row) => `${row.currency_code ?? currency} ${row.line_total}`,
          },
          {
            key: 'payment',
            label: 'Payment',
            render: (row) => formatSaleHistoryPayment(row.payments),
          },
          {
            key: 'reprint',
            label: '',
            className: 'text-right w-28',
            headerClassName: 'text-right',
            render: (row) => {
              const isBusy = busySaleId === row.sale_id
              return (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isBusy}
                  onClick={() => reprintReceipt(row.sale_id)}
                >
                  {isBusy && busyAction === 'receipt' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Receipt className="h-3.5 w-3.5" />
                  )}
                  Reprint
                </Button>
              )
            },
          },
        ]
      : [
          {
            key: 'completed_at',
            label: 'Date',
            render: (row) => (
              <span className="whitespace-nowrap">{formatSaleHistoryDate(row.completed_at)}</span>
            ),
          },
          {
            key: 'sale_number',
            label: 'Receipt',
            render: (row) => <span className="font-medium">#{row.sale_number}</span>,
          },
          {
            key: 'customer',
            label: 'Guest',
            render: (row) => formatSaleHistoryGuest(row.customer),
          },
          {
            key: 'services',
            label: 'Services',
            render: (row) => (
              <span className="max-w-md truncate block" title={formatSaleHistoryServices(row.services)}>
                {formatSaleHistoryServices(row.services)}
              </span>
            ),
          },
          {
            key: 'payment',
            label: 'Payment',
            render: (row) => formatSaleHistoryPayment(row.payments),
          },
          {
            key: 'total',
            label: 'Total',
            className: 'text-right font-mono tabular-nums font-semibold',
            headerClassName: 'text-right',
            render: (row) => `${row.currency_code ?? currency} ${row.total}`,
          },
          {
            key: 'reprint',
            label: '',
            className: 'text-right w-28',
            headerClassName: 'text-right',
            render: (row) => {
              const isBusy = busySaleId === row.id
              return (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isBusy}
                  onClick={() => reprintReceipt(row.id)}
                >
                  {isBusy && busyAction === 'receipt' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Receipt className="h-3.5 w-3.5" />
                  )}
                  Reprint
                </Button>
              )
            },
          },
        ]

  const periodHint =
    bounds?.from && bounds?.to ? `${bounds.from} to ${bounds.to}` : null

  return (
    <div className="space-y-4">
      <SaleDocumentPrintHosts
        receipt={receipt}
        taxInvoice={taxInvoice}
        receiptRootRef={receiptRootRef}
        taxInvoiceRootRef={taxInvoiceRootRef}
      />

      <SalonReportFilterBar
        period={period}
        customFrom={customFrom}
        customTo={customTo}
        onPeriodChange={setPeriod}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        loading={loading}
      />

      {(showSearch || showCustomerFilter) && (
        <div className="salon-panel grid gap-4 p-4 md:grid-cols-2">
          {showSearch ? (
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Search
              </span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Receipt #, name, or phone"
                className="h-11 w-full rounded-xl border border-border/80 bg-background px-4 text-sm"
              />
            </label>
          ) : null}

          {showCustomerFilter ? (
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Customer
              </span>
              {customerId ? (
                <div className="flex h-11 items-center justify-between rounded-xl border border-border/80 bg-muted/30 px-3 text-sm">
                  <span className="truncate font-medium">{customerLabel}</span>
                  <button
                    type="button"
                    className="text-xs font-semibold text-primary"
                    onClick={clearCustomerFilter}
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="search"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder="Filter by guest name or phone…"
                    className="h-11 w-full rounded-xl border border-border/80 bg-background px-4 text-sm"
                  />
                  {customerOptions.length > 0 ? (
                    <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-xl border border-border/80 bg-card shadow-lg">
                      {customerOptions.map((customer) => (
                        <li key={customer.id}>
                          <button
                            type="button"
                            className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-muted/60"
                            onClick={() => selectCustomer(customer)}
                          >
                            <span className="font-medium">{customer.name}</span>
                            <span className="text-xs text-muted-foreground">{customer.phone}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              )}
            </label>
          ) : null}
        </div>
      )}

      {requireStylist && !stylistId ? (
        <div className="salon-panel px-4 py-10 text-center text-sm text-muted-foreground">
          Select a staff member to view their ticket history.
        </div>
      ) : (
        <SalonDataTable
          title={tableTitle}
          columns={columns}
          rows={tableRows}
          loading={loading}
          error={error || printError}
          getRowKey={(row) => row.rowKey ?? row.id}
          emptyLabel={emptyLabel}
          meta={meta}
          onPageChange={setPage}
          footerStart={periodHint}
        />
      )}
    </div>
  )
}
